import type { MarketplaceProperty } from 'components/marketplace/property/types';

interface GalleryListingPhoto {
  url: string;
  urlLg: string | null;
  urlSm: string | null;
  urlXl: string | null;
  urlXs: string | null;
  label?: never;
  isFloorPlan: false;
}

interface GalleryFloorPlanPhoto {
  url: string;
  urlLg?: never;
  urlSm?: never;
  urlXl?: never;
  urlXs?: never;
  label: string;
  isFloorPlan: true;
}

export type GalleryPhoto = GalleryListingPhoto | GalleryFloorPlanPhoto;

type GalleryPhotoProperty = Pick<
  MarketplaceProperty,
  'photos' | 'photosLg' | 'photosSm' | 'photosXl' | 'photosXs' | 'floorPlanPhotos'
>;

// Builds the ordered set of gallery photos for a property by appending any
// available floor plan photos after the standard listing photos. Consumers
// (GalleryPhotos, PhotoCarousel, Lightbox) share this helper so the gallery
// index -> URL mapping stays consistent across surfaces; a click on the Nth
// tile in the gallery opens the same Nth photo in the lightbox.
export const buildGalleryPhotos = (property: GalleryPhotoProperty): GalleryPhoto[] => {
  const listingPhotos: GalleryListingPhoto[] = property.photos.map((url, index) => ({
    url,
    urlLg: property.photosLg[index] ?? null,
    urlSm: property.photosSm[index] ?? null,
    urlXl: property.photosXl[index] ?? null,
    urlXs: property.photosXs[index] ?? null,
    isFloorPlan: false,
  }));
  const floorPlanPhotos: GalleryFloorPlanPhoto[] = property.floorPlanPhotos.map((photo) => ({
    url: photo.url,
    label: photo.label,
    isFloorPlan: true,
  }));
  return [...listingPhotos, ...floorPlanPhotos];
};

// Photos for carousels backed by bricks-next `PhotoCarousel`. Its internal
// `Image` uses `resizeMode: cover` with no per-photo override, so a
// naturally-portrait floor plan gets cover-cropped to fill the square/16:9
// slide regardless of which URL variant we pass. Until bricks-next exposes
// per-photo resize, keep floor plans out of those carousels and surface
// them in the rendering paths we control (desktop GalleryPhotos, mobile
// "see all photos" modal, Lightbox), all of which use resizeMode: contain.
export const buildCarouselPhotos = (property: GalleryPhotoProperty): GalleryPhoto[] =>
  buildGalleryPhotos(property).filter((p) => !p.isFloorPlan);
